import {
  AddComment,
  CloseFullscreen,
  Minimize,
  OpenInFull,
} from "@mui/icons-material";
import MarkdownRenderer, {
  type MarkdownRendererBlock,
} from "@rcb-plugins/markdown-renderer";
import { useAuth } from "@ssf/auth-components";
import { useEffect, useRef, useState } from "react";
import ChatBot, {
  ChatBotProvider,
  type Params,
  useChatWindow,
  useFlow,
  useMessages,
  useStyles,
} from "react-chatbotify";
import { useTranslation } from "react-i18next";

interface HistoryMessage {
  role: "user" | "model";
  content: string;
  created: string;
}

interface HistoryResponse {
  sessionId: number;
  messages: HistoryMessage[];
}

const MEMDB_URL = process.env.MEMDB_URL;
if (!MEMDB_URL) {
  throw new Error("env `MEMDB_URL` is not set");
}

async function fetchHistory(
  accessToken?: string,
): Promise<HistoryResponse | null> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(`${MEMDB_URL}/api/v2/chat/history`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      console.log("No chat history found");
      return null;
    }

    return (await response.json()) as HistoryResponse;
  } catch (error) {
    console.error("Failed to fetch chat history:", error);
    return null;
  }
}

async function clearSession(accessToken?: string): Promise<boolean> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  try {
    const response = await fetch(`${MEMDB_URL}/api/v2/chat/session`, {
      method: "DELETE",
      headers,
      credentials: "include",
    });
    return response.ok;
  } catch (error) {
    console.error("Failed to clear session:", error);
    return false;
  }
}

async function fetchStatus(): Promise<boolean> {
  try {
    const response = await fetch(`${MEMDB_URL}/api/v2/chat/status`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) return false;
    const data = (await response.json()) as { available: boolean };
    return data.available;
  } catch (error) {
    console.error("Failed to fetch chat status:", error);
    return false;
  }
}

async function fetchChat(
  userMessage: string,
  accessToken?: string,
): Promise<string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  console.log("Fetching:", `${MEMDB_URL}/api/v2/chat`);
  console.log(
    "Request body:",
    JSON.stringify({ message: userMessage, url: window.location.href }),
  );

  const response = await fetch(`${MEMDB_URL}/api/v2/chat`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({
      message: userMessage,
      url: window.location.href,
    }),
  });

  console.log("Response status:", response.status);
  console.log(
    "Response headers:",
    Object.fromEntries(response.headers.entries()),
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.log("Error response body:", errorText);
    throw new Error(`Failed to get response: ${response.status}`);
  }

  const data = (await response.json()) as { message: string };
  console.log("Response message:", data.message);

  if (!data.message) {
    throw new Error("Empty response");
  }

  return data.message;
}

export const TornetChat = () => {
  const { t } = useTranslation("TornetChat");
  const { user } = useAuth();
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    fetchStatus().then(setIsAvailable);
  }, []);

  const flow: Record<string, MarkdownRendererBlock> = {
    start: {
      message: async (params: Params) => {
        await params.injectMessage(t("greeting"));
        return "";
      },
      path: "user_input",
      renderMarkdown: ["BOT"],
    },
    user_input: {
      message: async (params: Params) => {
        const userMessage = params.userInput;

        try {
          const reply = await fetchChat(userMessage, user?.access_token);

          // Simulate streaming for the typing effect
          await params.simulateStreamMessage(reply);
          return "";
        } catch (error) {
          console.error("Chat error:", error);
          return t("error");
        }
      },
      path: "user_input",
      renderMarkdown: ["BOT"],
    },
  };

  const settings = {
    general: {
      embedded: false,
      showFooter: false,
      flowStartTrigger: "ON_CHATBOT_INTERACT" as const,
    },
    header: {
      title: t("headerTitle"),
      showAvatar: true,
      avatar: "/tornet.png",
      buttons: [
        <NewChatButton key="new-chat" accessToken={user?.access_token} />,
        <ExpandButton key="expand" />,
        <CloseButton key="close" />,
      ],
    },
    chatButton: {
      icon: "/tornet.png",
    },
    chatWindow: {
      showScrollbar: true,
    },
    botBubble: {
      showAvatar: true,
      avatar: "/tornet.png",
      simulateStream: true,
    },
    notification: {
      disabled: true,
    },
    chatHistory: {
      disabled: true,
      storageKey: "tornet-chat-history",
    },
    tooltip: {
      text: t("tooltip"),
    },
    audio: {
      disabled: true,
    },
    fileAttachment: {
      disabled: true,
    },
    chatInput: {
      enabledPlaceholderText: t("placeholder"),
    },
  };

  const styles = {
    chatButtonStyle: {
      width: "72px",
      height: "80px",
      background: "none",
      border: "none",
      boxShadow: "none",
      borderRadius: "0",
      overflow: "visible",
    },
    chatIconStyle: {
      width: "72px",
      height: "80px",
      objectFit: "contain" as const,
      borderRadius: "0",
    },
    headerStyle: {
      background: "#bf175d",
      padding: "10px 15px",
    },
    chatWindowStyle: {
      width: "320px",
      height: "400px",
    },
    bodyStyle: {
      overflowX: "hidden" as const,
    },
    tooltipStyle: {
      background: "#bf175d",
      color: "#fff",
      fontSize: "12px",
      padding: "6px 10px",
      borderRadius: "6px",
      right: "90px",
      left: "unset",
    },
    sendButtonStyle: {
      background: "#bf175d",
    },
    sendButtonHoveredStyle: {
      background: "#a01350",
    },
    botBubbleStyle: {
      background: "#fff",
      color: "#000",
      border: "1px solid #bf175d",
      wordBreak: "break-word" as const,
      overflowWrap: "anywhere" as const,
      overflowX: "hidden" as const,
    },
    userBubbleStyle: {
      background: "#fff",
      color: "#000",
      border: "1px solid #1976d2",
      wordBreak: "break-word" as const,
      overflowWrap: "anywhere" as const,
      overflowX: "hidden" as const,
    },
    chatInputContainerStyle: {
      borderColor: "#bf175d",
    },
    chatInputAreaStyle: {
      borderColor: "#bf175d",
    },
    chatInputAreaFocusedStyle: {
      borderColor: "#bf175d",
      boxShadow: "0 0 0 2px rgba(191, 23, 93, 0.2)",
    },
  };

  if (!isAvailable) {
    return null;
  }

  return (
    <ChatBotProvider>
      <ChatHistoryLoader accessToken={user?.access_token} />
      <ChatBot
        flow={flow}
        settings={settings}
        styles={styles}
        plugins={[MarkdownRenderer()]}
      />
    </ChatBotProvider>
  );
};

const ChatHistoryLoader = ({ accessToken }: { accessToken?: string }) => {
  const { injectMessage } = useMessages();
  const { hasFlowStarted } = useFlow();
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!hasFlowStarted || loadedRef.current) return;
    loadedRef.current = true;

    const loadHistory = async () => {
      const history = await fetchHistory(accessToken);
      if (!history || history.messages.length === 0) return;

      for (const msg of history.messages) {
        await injectMessage(msg.content, msg.role === "user" ? "user" : "bot");
      }
    };

    loadHistory();
  }, [hasFlowStarted, accessToken, injectMessage]);

  return null;
};

const NewChatButton = ({ accessToken }: { accessToken?: string }) => {
  const { restartFlow } = useFlow();
  const { replaceMessages } = useMessages();

  const handleNewChat = async () => {
    const success = await clearSession(accessToken);
    if (success) {
      replaceMessages([]);
      await restartFlow();
    }
  };

  return (
    <button
      type="button"
      onClick={handleNewChat}
      style={{
        background: "none",
        border: "none",
        color: "#fff",
        cursor: "pointer",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
      }}
      title="Ny chatt"
    >
      <AddComment fontSize="small" />
    </button>
  );
};

const ExpandButton = () => {
  const { styles, updateStyles } = useStyles();
  const isExpanded =
    styles.chatWindowStyle?.width === "450px" ||
    styles.chatWindowStyle?.width === 450;

  const toggleExpand = () => {
    updateStyles({
      chatWindowStyle: isExpanded
        ? { width: "320px", height: "400px" }
        : { width: "450px", height: "550px" },
    });
  };

  return (
    <button
      type="button"
      onClick={toggleExpand}
      style={{
        background: "none",
        border: "none",
        color: "#fff",
        cursor: "pointer",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
      }}
      title={isExpanded ? "Förminska" : "Förstora"}
    >
      {isExpanded ? (
        <CloseFullscreen fontSize="small" />
      ) : (
        <OpenInFull fontSize="small" />
      )}
    </button>
  );
};

const CloseButton = () => {
  const { toggleChatWindow } = useChatWindow();

  return (
    <button
      type="button"
      onClick={() => toggleChatWindow(false)}
      style={{
        background: "none",
        border: "none",
        color: "#fff",
        cursor: "pointer",
        padding: "4px 8px",
        display: "flex",
        alignItems: "center",
      }}
      title="Stäng"
    >
      <Minimize fontSize="small" />
    </button>
  );
};
